import type { ChangeEvent } from 'react'
import Debug from 'debug'
import { action, makeAutoObservable, observable, reaction } from 'mobx'
import { retry } from 'radash'
import { callUnaryMethod } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import { getOrRefreshAccessToken } from '@jotta/auth-client/useAuthStatus'
import { createAuthTransport } from '@jotta/grpc-connect-client/transport'
import {
  allocatePhoto,
  forcePhotoImport,
} from '@jotta/grpc-connect-openapi/photoQuery'
import type { PromiseQueue } from '@jotta/promiseq'
import { queue } from '@jotta/promiseq'
import { AbortError } from '@jotta/types/AbortError'
import { AppError } from '@jotta/types/AppError'
import { handleUnknownError } from '@jotta/types/AppError'
import type { Photos } from '@jotta/types/Photos'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { calculateFileMd5 } from '@jotta/utils/crypto'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import type { FileMeta } from '@jotta/utils/files'
import { fileUploadXhr } from '@jotta/utils/fileUploadXhr'
import { nextXid } from '@jotta/utils/xid'
import { mutex } from 'async-mutex-lite'

import type { PhotoStore } from '../store/PhotoStore'

import { fetchPhotoById } from './photosApi'

type Status = 'queued' | 'pending' | 'resolved' | 'rejected' | 'aborted'
export type PhotoUploadCounts = Record<Status, number>

export class PhotoUpload {
  private _abortController = new AbortController()
  private _uploadProgress: number = 0
  private _response: Photos.Media | null = null
  private _error: AppError | null = null
  private log: Debug.Debugger
  public status: Status = 'queued'
  public statusUpdatedAt = Date.now()

  public get type() {
    return this._file.type
  }

  public get size() {
    return this._file.size
  }

  public get progress() {
    return this._uploadProgress
  }

  constructor(
    private _file: File,
    private _queue: PromiseQueue,
    public albumId = '',
  ) {
    this.log = Debug(`jotta:photos:upload:${this.file.name}`)
    this.log.enabled = true

    this.signal.addEventListener('abort', () => {
      this.setStatus('aborted')
    })

    const abortCheck = () => {
      if (this.signal.aborted) {
        throw new AbortError()
      }
    }

    const allocateAndUpload = async (md5: string) => {
      abortCheck()
      const xid = nextXid()
      const transport = createAuthTransport({
        xid,
        timeoutMs: 25_000,
      })
      const allocationResponse = await callUnaryMethod(
        transport,
        allocatePhoto,
        {
          md5,
          filename: this.file.name,
          size: BigInt(this.file.size),
          createdAtMillis: BigInt(this.file.lastModified || Date.now()),
          modifiedAtMillis: BigInt(this.file.lastModified || Date.now()),
        },
        {
          signal: this.signal,
        },
      )
      this.log('Allocate done: ', allocationResponse)
      const allocationResult = allocationResponse.result
      switch (allocationResult.case) {
        case 'completed':
          break
        case 'upload':
          this.log('Start upload')
          this.uploadProgress = 0
          abortCheck()
          await fileUploadXhr(
            this._file,
            allocationResult.value.uploadUrl,
            await getOrRefreshAccessToken(),
            xid,
            progressEvent => {
              this.uploadProgress = progressEvent.loaded
              this.log('Progress update', this._uploadProgress)
            },
            this.signal,
          )
          break
        case 'unsupported':
          throw new AppError({
            message: t`photo not supported`,
            xid,
          })
        case undefined:
          throw new AppError({
            message: 'unrecognized allocation result',
            xid,
          })
        default:
          exhaustiveGuard(allocationResult)
      }

      abortCheck()

      // retry on timeouts and network issues
      const forcePhotoImportResponse = await retry(
        {
          times: 2,
          delay: 1000,
        },
        () =>
          callUnaryMethod(
            transport,
            forcePhotoImport,
            {
              ref: {
                ref: {
                  case: 'md5',
                  value: md5,
                },
              },
            },
            {
              signal: this.signal,
            },
          ),
      )

      const photoImportResult = forcePhotoImportResponse.result
      switch (photoImportResult.case) {
        case 'importedPhoto': {
          const photo = photoImportResult.value
          this.response = await fetchPhotoById(photo.idDeprecated)
          this.setStatus('resolved')
          return
        }
        case 'notSupported':
          throw new AppError({
            message: t`photo not supported`,
            xid,
          })
        case 'notAbleToProcess':
          throw new AppError({
            message: t`not able to process photo`,
            xid,
          })
        case 'notFound':
        case undefined:
          throw new AppError({
            message: `photo upload failed - ${photoImportResult.case}`,
            xid,
          })
        default:
          exhaustiveGuard(photoImportResult)
      }
    }

    const fn = async () => {
      abortCheck()

      this.setStatus('pending')
      this.log('upload pending')

      const md5 = await calculateFileMd5({
        file: this.file,
        chunkSizeStr: '1mb',
        signal: this.signal,
      })
      this.log('Hash:', md5)
      abortCheck()

      await mutex(md5, () => allocateAndUpload(md5))
    }

    this._queue.schedule(fn, this.signal).catch(e => {
      return this.handleError(e)
    })

    makeAutoObservable<
      typeof this,
      'setResponse' | 'setStatus' | '_file' | '_reader'
    >(
      this,
      {
        _file: observable.ref,
        setResponse: action.bound,
        setStatus: action.bound,
        _reader: observable.ref,
        // AbortSignal is stable for the upload's lifetime — making it a
        // computed adds nothing and triggers MobX "read outside reactive
        // context" warnings on every non-reactive access (xhr/fetch
        // `signal: this.signal`, addEventListener, etc.).
        signal: false,
      },
      { autoBind: true },
    )
  }

  handleError(e: unknown) {
    const error = handleUnknownError(e)
    this.log('error', error.name, error.status)
    this._error = error
    if (this.status !== ('aborted' as Status)) {
      this.setStatus('rejected')
    }
    return null
  }

  get thumb() {
    const url = this._response?.thumbnail_url
    if (url) {
      return `${url}.s`
    }
    return ''
  }

  private setStatus(status: Status) {
    this.status = status
    this.statusUpdatedAt = Date.now()
  }

  public get id() {
    return this.response?.id || ''
  }

  public get file() {
    return this._file
  }

  public get filename() {
    return this._file.name
  }

  public get signal() {
    return this._abortController.signal
  }

  public cancel() {
    this._abortController.abort()
  }

  public get response() {
    return this._response
  }

  private set response(response: Photos.Media | null) {
    this._response = response
  }

  public get error() {
    return this._error || undefined
  }

  public set uploadProgress(progress: number) {
    this._uploadProgress = progress
  }
}

export class PhotoUploadCollection {
  private queue: PromiseQueue
  public uploads = observable.array<PhotoUpload>([])
  private rejections = observable.array<string>([])

  constructor(public photo: PhotoStore) {
    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    )

    this.queue = queue
    reaction(
      () => [this.hasCompleted, this.total],
      () => {
        if (this.hasCompleted && this.total > 0) {
          if (this.uploadIdsByAlbumId) {
            Promise.all(
              Object.entries(this.uploadIdsByAlbumId).map(([albumId, ids]) =>
                this.photo.actions.addSelectedPhotosToAlbum(albumId, ids),
              ),
            )
          }
        }
      },
    )
  }

  public upload(files: FileMeta[]) {
    const albumId = this.photo.currentAlbumId
    const uploads = files.map(
      file => new PhotoUpload(file.file, this.queue, albumId),
    )

    for (const upload of uploads) {
      reaction(
        () => [upload.status],
        () => {
          if (upload.status === 'resolved' && upload.response) {
            const photo = upload.response
            const photos = [photo]
            this.photo.mediaObjects.setPhotos(photos)
            this.photo.timeline.addFromUpload(photos)
          }
        },
      )
    }

    this.uploads.push(...uploads)

    return uploads
  }

  public onReject(paths: string[]) {
    this.rejections.push(...paths)
  }

  public onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files?.length) {
      this.upload(Array.from(event.target.files).map(file => ({ file })))
      event.target.value = ''
    }
  }

  public get total() {
    return this.uploads.length
  }

  public get completed() {
    return this.uploads.filter(upload => upload.status === 'resolved').length
  }

  public get hasCompleted() {
    return Boolean(
      this.uploads.every(
        upload => !['queued', 'pending'].includes(upload.status),
      ),
    )
  }

  public get pending() {
    return this.uploads.find(u => u.status === 'pending')
  }

  public get thumb() {
    let thumb = ''
    for (let i = this.uploads.length - 1; i >= 0; i--) {
      const upload = this.uploads[i]
      if (upload.status === 'resolved' && upload.thumb) {
        thumb = upload.thumb
        break
      }
    }
    return thumb
  }

  public get type() {
    return this.pending?.type || ''
  }

  public get pendingUploads() {
    return this.uploads.filter(upload =>
      ['pending', 'queued'].includes(upload.status),
    )
  }

  public cancel() {
    for (const upload of this.pendingUploads) {
      upload.cancel()
    }
  }

  public clear() {
    this.cancel()
    this.uploads.clear()
    this.rejections.clear()
  }

  public get status() {
    return createPhotoUploadStats(
      this.uploads,
      this.rejections,
      this.type,
      this.thumb,
    )
  }

  public get uploadIdsByAlbumId() {
    if (this.status.uploadsCompleted) {
      return this.status.idsByAlbum
    }
    return null
  }
}

export function createPhotoUploadStats(
  uploads: PhotoUpload[] = [],
  rejections: string[],
  type: string,
  thumbSrc = '',
) {
  const rejected = rejections.length
  const total = uploads.length + rejected
  const idsByAlbum: Record<string, string[]> = {}
  const stats = {
    queued: 0,
    pending: 0,
    resolved: 0,
    rejected,
    aborted: 0,
    progress: 0,
    total,
    ids: [] as string[],
    errors: [] as {
      filename: string
      message: string
      type: string
      xid: string
      code: number
    }[],
    type,
    thumbSrc,
    get state() {
      if (this.isIdle) {
        return 'idle'
      }
      if (this.isUploading) {
        return 'uploading'
      }
      if (this.uploadsCompletedSuccessfully) {
        return 'uploadsCompleted'
      }
      if (this.uploadsCompletedWithErrors) {
        return 'uploadsCompletedWithErrors'
      }
      if (this.hasErrors) {
        return 'error'
      }
    },
    get isIdle() {
      return !this.hasUploads
    },
    get uploading() {
      return this.queued + this.pending
    },
    get failed() {
      return this.aborted + this.rejected
    },
    get isUploading() {
      return Boolean(this.uploading)
    },
    // All uploads completed, possibly with errors
    get uploadsCompleted() {
      return !this.isUploading && this.hasUploads
    },
    // All uploads completed without errors
    get uploadsCompletedSuccessfully() {
      return this.resolved === this.total
    },
    // All uploads completed, at least one successfully, some have errors
    get uploadsCompletedWithErrors() {
      return !this.uploading && this.hasSuccessfulUploads && this.hasErrors
    },
    get hasUploads() {
      return Boolean(this.total)
    },
    get hasSuccessfulUploads() {
      return Boolean(this.resolved)
    },
    get hasErrors() {
      return Boolean(this.rejected || this.aborted)
    },
  }
  let totalSize = 0
  let completed = 0
  const timeline: string[] = []

  for (const upload of uploads) {
    const { id, error, filename = 'unknown' } = upload
    if (error) {
      stats.errors.push({
        filename,
        code: error.status || 0,
        message: error.message,
        xid: error.xid || 'unknown',
        type: error.name,
      })
    }
    if (id) {
      stats.ids.push(id)
      const albumId = upload.albumId
      if (albumId) {
        const arr = idsByAlbum[albumId] || []
        arr.push(id)
        idsByAlbum[albumId] = arr
      } else {
        timeline.push(id)
      }
    }
    stats[upload.status]++
    totalSize += upload.size
    completed += upload.progress
    stats.progress = totalSize ? completed / totalSize : 0
  }

  return {
    ...stats,
    idsByAlbum,
    isAlbumUpload: !timeline.length,
  }
}
